import { buildDateVariables } from 'src/apps/dashboard/services/queries/dashboard/helpers/dateVariableBuilder';
import { DashboardDataResult } from 'src/apps/dashboard/services/queries/dashboard/helpers/transformResults';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { LabelsExcludedFromDashboard } from 'src/common/types/ConfigurationTypes';

import {
	MobileDashboardDataQuery,
	MobileDashboardDataQueryVariables
} from '../MobileDashboardData.generated';

import { buildMobileBaseVariables } from './mobileFilterVariableBuilder';

export const buildMobileDashboardDataVariables = (
	days: number,
	filters?: IFilterOptions,
	labelsExcludedFromDashboard?: LabelsExcludedFromDashboard
): MobileDashboardDataQueryVariables => {
	const dateVariables = buildDateVariables(days);
	const mobileFilters = buildMobileBaseVariables(filters, labelsExcludedFromDashboard);

	return {
		days: dateVariables.days,
		days2n: dateVariables.days2n,
		...mobileFilters
	};
};

type MfaIssueRow = NonNullable<NonNullable<MobileDashboardDataQuery['mfaIssuesCount']>[number]>;
type MsdkIssueRow = NonNullable<NonNullable<MobileDashboardDataQuery['msdkIssuesCount']>[number]>;

const hasValidMfaDate = (
	issue: MfaIssueRow
): issue is MfaIssueRow & { run_updated_at_truncDay: string } =>
	typeof issue.run_updated_at_truncDay === 'string';

const hasValidMsdkDate = (
	issue: MsdkIssueRow
): issue is MsdkIssueRow & { run_time_truncDay: string } =>
	typeof issue.run_time_truncDay === 'string';

const processMfaIssues = (
	issues: MobileDashboardDataQuery['mfaIssuesCount'],
	dateMap: Map<string, { critical: number; serious: number; total: number; allIssues: number }>
): void => {
	(issues ?? []).filter(hasValidMfaDate).forEach((issue) => {
		const date = issue.run_updated_at_truncDay;
		const existing = dateMap.get(date) ?? { critical: 0, serious: 0, total: 0, allIssues: 0 };
		const critical = existing.critical + (issue.critical_issues_sum ?? 0);
		const serious = existing.serious + (issue.serious_issues_sum ?? 0);
		dateMap.set(date, {
			critical,
			serious,
			total: critical + serious,
			allIssues: existing.allIssues + (issue.total_issues_sum ?? 0)
		});
	});
};

const processMsdkIssues = (
	issues: MobileDashboardDataQuery['msdkIssuesCount'],
	dateMap: Map<string, { critical: number; serious: number; total: number; allIssues: number }>
): void => {
	(issues ?? []).filter(hasValidMsdkDate).forEach((issue) => {
		const date = issue.run_time_truncDay;
		const existing = dateMap.get(date) ?? { critical: 0, serious: 0, total: 0, allIssues: 0 };
		const critical = existing.critical + (issue.critical_issues_sum ?? 0);
		const serious = existing.serious + (issue.serious_issues_sum ?? 0);
		dateMap.set(date, {
			critical,
			serious,
			total: critical + serious,
			allIssues: existing.allIssues + (issue.total_issues_sum ?? 0)
		});
	});
};

const combineMobileProductData = (
	mfaData: MobileDashboardDataQuery['mfaIssuesCount'],
	msdkData: MobileDashboardDataQuery['msdkIssuesCount']
): DashboardDataResult['regularPeriod'] => {
	const dateMap = new Map<
		string,
		{ critical: number; serious: number; total: number; allIssues: number }
	>();

	processMfaIssues(mfaData, dateMap);
	processMsdkIssues(msdkData, dateMap);

	return Array.from(dateMap.entries())
		.map(([dateStr, counts]) => ({
			critical: counts.critical,
			serious: counts.serious,
			total: counts.critical + counts.serious,
			allIssues: counts.allIssues,
			day: new Date(dateStr)
		}))
		.sort((a, b) => a.day.getTime() - b.day.getTime());
};

const sumSummaryValues = (
	mfaSummary: MobileDashboardDataQuery['mfaSummary'],
	msdkSummary: MobileDashboardDataQuery['msdkSummary']
): { critical: number; total: number } => {
	const mfaCritical = mfaSummary?.[0]?.critical_issues_sum ?? 0;
	const mfaTotal = mfaSummary?.[0]?.total_issues_sum ?? 0;
	const msdkCritical = msdkSummary?.[0]?.critical_issues_sum ?? 0;
	const msdkTotal = msdkSummary?.[0]?.total_issues_sum ?? 0;

	return {
		critical: mfaCritical + msdkCritical,
		total: mfaTotal + msdkTotal
	};
};

const getLastUpdatedAt = (rawResults: MobileDashboardDataQuery): number => {
	const mfaTimestamp = rawResults.mfaSummary?.[0]?.run_updated_at_max;
	const msdkTimestamp = rawResults.msdkSummary?.[0]?.run_time_max;
	const timestamps = [mfaTimestamp, msdkTimestamp]
		.filter(Boolean)
		.map((value) => new Date(value as string).getTime());

	return timestamps.length > 0 ? Math.max(...timestamps) : 0;
};

export const transformMobileDashboardDataResults = (
	rawResults: MobileDashboardDataQuery
): DashboardDataResult => {
	const regularPeriod = combineMobileProductData(
		rawResults.mfaIssuesCount,
		rawResults.msdkIssuesCount
	);
	const doublePeriod = combineMobileProductData(
		rawResults.mfaIssuesCount2n,
		rawResults.msdkIssuesCount2n
	);

	const summary = sumSummaryValues(rawResults.mfaSummary, rawResults.msdkSummary);
	const uniqueIssues = {
		current: summary.total,
		previous: 0,
		critical: {
			current: summary.critical,
			previous: 0
		}
	};

	const dailyUniqueIssues = regularPeriod.map((entry) => ({
		date: entry.day.toISOString(),
		total: entry.total,
		critical: entry.critical,
		serious: entry.serious
	}));

	return {
		regularPeriod,
		doublePeriod,
		uniqueIssues,
		totalIssues: uniqueIssues,
		dailyUniqueIssues,
		dailyAllIssues: dailyUniqueIssues.map((entry) => ({
			...entry,
			total: entry.total
		})),
		lastUpdatedAt: getLastUpdatedAt(rawResults)
	};
};
